import { useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import { useParams } from 'react-router';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BasePage } from 'hydra/pages/BasePage';
import { UserProfile } from 'user/components/UserProfile';
import {
    IRole,
    CurrentUserDocument,
    PortfolioSettingsDocument,
    UserDocument,
} from 'graphql/types';
import { ApolloError } from 'common/components/ApolloError';
import { Loader } from 'common/components/Loader';
import { PageIntroHeader } from 'common/components/PageIntroHeader';
import { Avatar } from 'common/components/Avatar';
import { Text } from 'common/components/Text';
import { useCanManageUser, useFrontendPermissions } from 'user/hooks';
import { Link } from 'common/components/Link';
import { useApolloError } from 'common/hooks/useApolloError';
import { Chip, ChipGroup } from 'common/components/Chip';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { ManageButton } from 'common/components/Button/ManageButton';
import { usePortfolioViewPermissions } from 'user/hooks/portfolio/usePortfolioViewPermissions';
import { UpgradeContent as PortfolioUpgradeContent } from 'user/components/UpgradeContent';
import { UpgradeContent as OffersUpgradeContent } from 'offer/components/UpgradeContent';

import { FourOFourPage } from './FourOFourPage';

export const UserPage = () => {
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const { openGlobalDrawer } = useGlobalDrawer();
    const { id } = useParams();
    const {
        canUsePortfolioOffers,
        showUpgradePage,
        isPortfolioUpgradePage,
        loading: loadingViewPermissions,
        pageNotFound,
        hideOffersSection,
        hideMeetingsSection,
        canUseSkills,
    } = usePortfolioViewPermissions();

    const [translate] = useTranslation();

    const { canManageUser, loading: loadingCanManageUser } =
        useCanManageUser(id);

    const [
        loadUser,
        { data, loading: userLoading, called: userCalled, error },
    ] = useLazyQuery(UserDocument);
    const {
        data: portfolioSettingsData,
        loading: portfolioSettingsLoading,
        error: portfolioSettingsError,
    } = useQuery(PortfolioSettingsDocument);
    const {
        canUpdate: canUpdatePortfolioSettings,
        loading: loadingPermissions,
    } = useFrontendPermissions('portfolioSettings');
    const { showApolloError } = useApolloError();

    useEffect(() => {
        if (id && !userLoading && !error && data?.user?.id !== id) {
            loadUser({
                variables: { id },
            });
        }
    }, [loadUser, userLoading, error, data, id]);

    useEffect(() => {
        if (portfolioSettingsError) showApolloError(portfolioSettingsError);
    }, [portfolioSettingsError, showApolloError]);

    const { currentUser } = currentUserData || {};
    const isCurrentUser = !id || id === currentUser?.id;
    const user = id ? data?.user : currentUser;
    const userNotFound = userCalled && !userLoading && !user;

    if (userNotFound || pageNotFound) return <FourOFourPage />;

    const { portfolioSettings } = portfolioSettingsData || {};

    const { title, titleColor, description, image, published } =
        portfolioSettings || {};

    // If the portfolio is published or
    // if not published you need to have the roles to update the portfolio settings
    const currentUserCanViewProfilePage =
        published || (!published && canUpdatePortfolioSettings);

    // The roles a user needs to see another user's profile
    // userCalled is only called when checking another user's profile
    // This check is always false when checking your own profile
    const currentUserCanViewOtherUser =
        !userCalled ||
        (userCalled &&
            (data?.user?.currentUserIsGroupManager ||
                currentUser?.roles?.includes(IRole.Manager)));

    if (
        !portfolioSettingsLoading &&
        !userLoading &&
        (!currentUserCanViewOtherUser || !currentUserCanViewProfilePage)
    ) {
        return <FourOFourPage />;
    }

    let header;

    const actionButtons = ((!loadingCanManageUser && canManageUser) ||
        isCurrentUser) && (
        <ManageButton
            component={Link}
            outlined={!image}
            to={isCurrentUser ? 'PROFILE_DRAWER' : undefined}
            onClick={
                !isCurrentUser
                    ? () => {
                          openGlobalDrawer({
                              type: IGlobalDrawerType.UserProfile,
                              itemId: id,
                          });
                      }
                    : undefined
            }
        >
            {translate('profile')}
        </ManageButton>
    );

    let headerColor = titleColor;

    if (!headerColor) {
        headerColor = !image ? undefined : 'white.main';
    }

    const hiddenChip = !published && (
        <Box sx={{ mb: 2 }}>
            <ChipGroup>
                <Chip bgColor="warning" label={translate('hidden')} />
            </ChipGroup>
        </Box>
    );

    const userDisplay = user && (
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: 2 }}>
                <Avatar
                    src={user?.profileImage.url || ''}
                    sx={{ border: '4px solid #fff', width: 85, height: 85 }}
                >
                    {user?.name[0] || ''}
                </Avatar>
            </Box>

            <Box>
                {title && (
                    <Box sx={{ mr: 1 }}>
                        <Typography
                            style={{ color: headerColor }}
                            variant="subtitle1"
                        >
                            {title}
                        </Typography>
                    </Box>
                )}

                <Typography sx={{ color: headerColor }} variant="h1">
                    {user.name}
                </Typography>
            </Box>
        </Box>
    );

    if (image) {
        header = (
            <PageIntroHeader contentContainerMaxWidth="md" image={image}>
                {hiddenChip}

                <Grid
                    container
                    spacing={2}
                    sx={{
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid>{userDisplay}</Grid>

                    <Box sx={{ mt: { xs: 2, md: 0 } }}>{actionButtons}</Box>
                </Grid>
            </PageIntroHeader>
        );
    }

    const loading =
        userLoading ||
        portfolioSettingsLoading ||
        loadingViewPermissions ||
        loadingPermissions;

    return (
        <BasePage
            contentContainerMaxWidth={showUpgradePage ? 'lg' : undefined}
            header={showUpgradePage ? undefined : header}
        >
            {error && <ApolloError error={error} />}
            {loading && <Loader />}
            {!loading && user && !showUpgradePage && (
                <>
                    {!header && (
                        <Box sx={{ mt: 4 }}>
                            {hiddenChip}

                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    alignItems: !!title ? 'flex-end' : 'center',
                                    justifyContent: 'space-between',
                                    maxWidth: 'md',
                                }}
                            >
                                <Grid>{userDisplay}</Grid>

                                {!header && <Grid>{actionButtons}</Grid>}
                            </Grid>
                        </Box>
                    )}

                    {!!description && (
                        <Box sx={{ mb: 2, mt: 4 }}>
                            <Text variant="body1">{description}</Text>
                        </Box>
                    )}

                    <Box sx={{ mt: !description ? 2 : 0 }}>
                        <UserProfile
                            canUsePortfolioOffers={canUsePortfolioOffers}
                            canUseSkills={canUseSkills}
                            hideMeetingsSection={hideMeetingsSection}
                            hideOffersSection={hideOffersSection}
                            isCurrentUser={isCurrentUser}
                            user={user}
                        />
                    </Box>
                </>
            )}
            {showUpgradePage &&
                (isPortfolioUpgradePage ? (
                    <PortfolioUpgradeContent />
                ) : (
                    <OffersUpgradeContent />
                ))}
        </BasePage>
    );
};
